import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { useGetAdminAreaHierarchy } from "@/api/adminArea/userAdminAreaAPI";
import { useGetCountries } from "@/api/country/useCountryAPI";
import {
  useGetBrandsByCategoryMutation,
  useGetDatasetsByPoiParameters,
  useGetParametersQuery,
  useGetSearchDatasetsQuery
} from "@/api/poiParameter/usePoiParameterAPI";
import NotificationContent from "@/components/notification/notificationContent";
import Dropdown from "@/components/ui/dropdown";
import Modal from "@/components/ui/modal";
import SearchAutocomplete, {
  GroupedOption
} from "@/components/ui/searchAutocomplete";
import { COUNTRY_DATA } from "@/constants/dataset";
import { ERROR_MESSAGE } from "@/constants/message";
import { getSelectedAdminAreaCodesFromIds } from "@/helpers/datasetAttributeHelper";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDebounce } from "@/hooks/useDebounce";
import { RootState } from "@/store";
import { CountryCode } from "@/types/common";
import {
  BrandAction,
  PoiBrandCategoryGroup,
  PoiBrandGroupedByCategory,
  PoiBrandSearch,
  PoiCategory,
  PoiGroup,
  PoiLevel,
  PoiParameterResponse,
  PoiParameterState,
  PostPoiParametersPayload
} from "@/types/poiParameter";
import { AdminAreaSettings } from "@/types/spatial";

import { Preview } from "./Preview";
import { UnlockPremiumModal } from "./UnlockPremiumModal";

import { DatasetItem } from "./poi/DatasetItem";
import { IndustryRibbon } from "./poi/IndustryRibbon";
import { PoiBrandTreeSelector } from "./poi/PoiBrandTreeSelector";
import { PoiConfirmationView } from "./poi/PoiConfirmationView";
import { PoiSidebar } from "./poi/PoiSidebar";
import {
  getDeepestAdminAreaCode,
  groupPoiByLevel,
  isGlobalFilterAdminAreaSelected,
  POILevelLabelMap
} from "./poi/utils";

const TEST_ID_PREFIX = "poi-parameter-tab";

export const POIParameterTab = () => {
  const location = useLocation();
  const { poiParameterState: historyPoiParameterState } = location?.state ?? {};
  const { isAdminTreeEnabled, isGlobalFilterOverrideEnabled } =
    useAppFeatures();
  const { showError } = useLoadingAndToast();
  const queryClient = useQueryClient();

  const { data: countryData, isLoading: isLoadingCountryData } =
    useGetCountries(
      { subscribed: true },
      {
        select: (data) =>
          data?.filter((country) => country.name.toLowerCase() !== "global")
      }
    );

  const {
    adminAreaSettings: globalAdminAreaSettings,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);

  const countryOptions = useMemo(() => {
    if (!countryData) return [];
    return [...countryData]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((country) => ({
        label: country.name,
        value: country.id
      }));
  }, [countryData]);

  const [selectedCountry, setSelectedCountry] = useState<{
    label: string;
    value: CountryCode;
  } | null>(null);

  const [selectedAdminArea, setSelectedAdminArea] =
    useState<AdminAreaSettings | null>(null);
  const [selectedAdminAreaIds, setSelectedAdminAreaIds] = useState<string[]>(
    []
  );

  const { data: poiParameters, isLoading: isLoadingPoiParameters } =
    useGetParametersQuery((selectedCountry ?? countryOptions?.[0])?.value, {
      enabled: !!(selectedCountry ?? countryOptions?.[0])
    });

  // --- State ---
  const [activeIndustry, setActiveIndustry] = useState<string>("11");
  const [activeGroup, setActiveGroup] = useState<string>("1101");

  // View mode: selection or confirmation
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  // Expanded categories in the middle tree [compositeKey]
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set([])
  );

  // Selected Items Store: Record<CompositeKey, CategorySelection>
  const [selectedBrands, setSelectedBrands] =
    useState<PoiBrandGroupedByCategory>({});

  // For ordering categories selected in the sidebar
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set([])
  );

  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const debouncedBrandSearchQuery = useDebounce(brandSearchQuery, 300);
  const isBrandSearchQueryValid = debouncedBrandSearchQuery.length > 2;

  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    null
  );
  const [showPreview, setShowPreview] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedDatasetIds, setSelectedDatasetIds] = useState<string[]>([]);

  const { data: searchPOI } = useGetSearchDatasetsQuery(
    { query: debouncedBrandSearchQuery, perPage: 100 },
    {
      enabled: isBrandSearchQueryValid
    }
  );
  const [isLoadingBrands, setIsLoadingBrands] = useState<{
    [nodeId: string]: boolean;
  }>({});

  const [sidebarExpandedCategories, setSidebarExpandedCategories] = useState<{
    [categoryCode: string]: boolean;
  }>({});

  const [isOverrideGlobalFilter, setIsOverrideGlobalFilter] =
    useState<boolean>(false);

  const currentIndustry = poiParameters?.data?.find(
    (c) => c.code === activeIndustry
  );
  const currentGroup = currentIndustry?.poi_groups?.find(
    (g) => g.code === activeGroup
  );

  const changeSidebarCategoryExpand = (
    categoryCode: string,
    isExpanded: boolean
  ) => {
    setSidebarExpandedCategories((prev) => ({
      ...prev,
      [categoryCode]: isExpanded
    }));
  };

  const changeExpandCategory = (categoryCode: string, isExpanded: boolean) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(categoryCode);
      } else {
        next.delete(categoryCode);
      }
      return next;
    });
  };

  const changeSelectedCategories = (
    categoryCode: string,
    isSelected: boolean
  ) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(categoryCode);
      } else {
        next.delete(categoryCode);
      }
      return next;
    });
  };

  const { mutateAsync: getBrandsByCategoryMutation } =
    useGetBrandsByCategoryMutation();

  const updateCategories = (
    categories: PoiCategory[],
    categoryCode: string,
    brands: PoiBrandSearch[]
  ): PoiCategory[] => {
    return categories.map((category) =>
      category.code === categoryCode
        ? { ...category, poi_brands: brands }
        : category
    );
  };

  const attachBrandsToCategory = async ({
    countryId,
    categoryCode,
    group
  }: {
    countryId?: string;
    categoryCode: string;
    group?: PoiGroup;
  }) => {
    const groupToUse = group ?? currentGroup;
    const category = groupToUse?.poi_categories?.find(
      (c) => c.code === categoryCode
    );

    if (!category) return [];

    // Check if brands already fetched
    if (category.poi_brands !== undefined) {
      return category.poi_brands;
    }

    try {
      setIsLoadingBrands((prev) => ({ ...prev, [categoryCode]: true }));
      const data = await getBrandsByCategoryMutation({
        countryId,
        categoryCode,
        perPage: 999999 // to get all brands without pagination
      });

      queryClient.setQueryData(
        ["poi-parameters", countryId],
        (oldData: PoiParameterResponse): PoiParameterResponse => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data?.map((industry) => {
              if (industry.code !== groupToUse?.poi_industry_code)
                return industry;

              return {
                ...industry,
                poi_groups:
                  (industry.poi_groups ?? []).map((group) => {
                    if (group.code !== groupToUse?.code) return group;

                    return {
                      ...group,
                      poi_categories: updateCategories(
                        group.poi_categories ?? [],
                        categoryCode,
                        (data?.data ?? [])?.sort((a, b) =>
                          a.brand_name.localeCompare(b.brand_name)
                        )
                      )
                    };
                  }) ?? []
              };
            })
          };
        }
      );

      return data?.data ?? [];
    } catch (error) {
      console.error("Failed to fetch brands by category:", error);

      showError(
        <NotificationContent
          title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
          description="Failed to load brands. Please try again later."
        />
      );

      return [];
    } finally {
      setIsLoadingBrands((prev) => {
        const { [categoryCode]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const toggleCategoryExpand = (categoryCode: string) => {
    // Check if category is currently expanded
    const isCurrentlyExpanded = expandedCategories.has(categoryCode);

    // If currently expanded (collapsing action), just remove it without fetching
    if (isCurrentlyExpanded) {
      changeExpandCategory(categoryCode, false);
      return;
    }

    // If currently collapsed (expanding action), fetch brands first
    attachBrandsToCategory({
      countryId: (selectedCountry ?? countryOptions?.[0])?.value,
      categoryCode
    });

    // Add category to expanded set
    changeExpandCategory(categoryCode, true);
  };

  const addBrandToCategory = (
    categoryCode: string,
    categoryName: string,
    brand: PoiBrandSearch
  ) => {
    setSelectedBrands((prev) => {
      const newRecord = { ...prev };
      const currentSelection: PoiBrandCategoryGroup = newRecord[
        categoryCode
      ] || {
        categoryName,
        categoryCode,
        brands: []
      };

      // Add if not exists
      if (
        !currentSelection.brands.some((i) => i.brand_code === brand.brand_code)
      ) {
        newRecord[categoryCode] = {
          ...currentSelection,
          brands: [...currentSelection.brands, brand]
        };
      }

      return newRecord;
    });

    // Auto-expand category in sidebar when brand is selected
    changeSidebarCategoryExpand(categoryCode, true);
    changeSelectedCategories(categoryCode, true);
  };

  const removeBrandFromCategory = (categoryCode: string, brandCode: string) => {
    setSelectedBrands((prev) => {
      const newRecord = { ...prev };
      const currentSelection = newRecord[categoryCode];

      if (!currentSelection) return newRecord;

      const newItems = currentSelection.brands.filter(
        (i) => i.brand_code !== brandCode
      );

      // Delete category if no brands left
      if (newItems.length === 0) {
        delete newRecord[categoryCode];
        changeSelectedCategories(categoryCode, false);
      } else {
        newRecord[categoryCode] = { ...currentSelection, brands: newItems };
      }

      return newRecord;
    });
  };

  const handleSelectBrand = (
    categoryCode: string,
    categoryName: string,
    brand: PoiBrandSearch,
    action: BrandAction
  ) => {
    switch (action) {
      case "add":
        addBrandToCategory(categoryCode, categoryName, brand);
        break;
      case "remove":
        removeBrandFromCategory(categoryCode, brand.brand_code);
        break;
    }
  };

  const handleSelectCategory = async (
    categoryCode: string,
    categoryName: string,
    isChecked: boolean,
    group?: PoiGroup
  ) => {
    // Uncheck
    if (!isChecked) {
      handleDeleteCategory(categoryCode);
      changeSelectedCategories(categoryCode, false);
      return;
    }

    // Lazy load brands if needed (attachBrandsToCategory checks if already loaded)
    const loadedBrands =
      (await attachBrandsToCategory({
        countryId: (selectedCountry ?? countryOptions?.[0])?.value,
        categoryCode,
        group
      })) || [];

    if (loadedBrands.length === 0) return;

    setSelectedBrands((prev) => ({
      ...prev,
      [categoryCode]: {
        categoryCode,
        categoryName,
        brands: loadedBrands
      }
    }));

    // Auto-expand category in sidebar when category is selected
    changeSidebarCategoryExpand(categoryCode, true);
    changeSelectedCategories(categoryCode, true);
  };

  const handleDeleteCategory = (categoryCode: string) => {
    setSelectedBrands((prev) => {
      const newRecord = { ...prev };
      delete newRecord[categoryCode];
      return newRecord;
    });
    changeSelectedCategories(categoryCode, false);
  };

  const handleDeleteBrand = (categoryCode: string, itemId: string) => {
    setSelectedBrands((prev) => {
      const newRecord = { ...prev };
      const selection = newRecord[categoryCode];
      if (selection) {
        const newItems = selection.brands.filter(
          (i) => i.brand_code !== itemId
        );
        if (newItems.length === 0) {
          delete newRecord[categoryCode];
          changeSelectedCategories(categoryCode, false);
        } else {
          newRecord[categoryCode] = { ...selection, brands: newItems };
        }
      }
      return newRecord;
    });
  };

  const toggleSidebarExpand = (key: string) => {
    changeSidebarCategoryExpand(key, !sidebarExpandedCategories[key]);
  };

  const handleSyncSelectionGlobalAdminArea = useCallback(() => {
    setSelectedAdminArea(globalAdminAreaSettings);
    setSelectedAdminAreaIds(globalSelectedAreaIds);
  }, [globalAdminAreaSettings, globalSelectedAreaIds]);

  // sync with global admin area filter
  useEffect(() => {
    if (
      globalAdminAreaSettings?.lv0?.value !==
      (selectedCountry ?? countryOptions?.[0])?.value
    )
      return;
    handleSyncSelectionGlobalAdminArea();
  }, [
    globalAdminAreaSettings,
    globalSelectedAreaIds,
    selectedCountry,
    countryOptions,
    handleSyncSelectionGlobalAdminArea
  ]);

  const isAdmAreaFilled = isAdminTreeEnabled
    ? selectedAdminAreaIds.length > 0
    : !!selectedAdminArea?.lv1;
  const isContinueDisabled =
    isLoadingPoiParameters ||
    Object.keys(selectedBrands).length === 0 ||
    !isAdmAreaFilled;

  const getFirstGroupCode = (industryCode: string) => {
    return poiParameters?.data
      .find((ind) => ind.code === industryCode)
      ?.poi_groups?.sort((a, b) => a.name.localeCompare(b.name))?.[0]?.code;
  };

  // Organize search results by hierarchy level (Groups, Categories, Brands)
  const organizedSearchResults = useMemo(() => {
    if (!searchPOI?.data) return [];
    const { groups, categories, brands } = groupPoiByLevel(searchPOI.data);

    const result: GroupedOption<PoiBrandSearch>[] = [];

    if (groups.length > 0) {
      result.push({ group: "Groups", items: groups });
    }
    if (categories.length > 0) {
      result.push({ group: "Categories", items: categories });
    }
    if (brands.length > 0) {
      result.push({ group: "Brands", items: brands });
    }

    return result;
  }, [searchPOI]);

  const renderSearchOptions = (item: PoiBrandSearch) => {
    const level = item.type ?? PoiLevel.BRAND;
    const [children, parent] = POILevelLabelMap[level](item);

    return (
      <div className="p-2 flex items-center gap-4">
        <div className="text-txtHeading text-sm">{children}</div>
        <div className="text-txtBody text-xs p-1 border border-gainsboro rounded-lg">
          {parent}
        </div>
      </div>
    );
  };

  const updateActiveIndustryAndGroup = (
    industryCode: string,
    groupCode: string
  ) => {
    setActiveIndustry(industryCode);
    setActiveGroup(groupCode);
  };

  const getTargetGroup = (option: PoiBrandSearch) => {
    const targetIndustry = poiParameters?.data?.find(
      (ind) => ind.code === option.industry_code
    );
    if (!targetIndustry) return;

    const targetGroup = targetIndustry?.poi_groups?.find(
      (grp) => grp.code === option.group_code
    );
    if (!targetGroup) return;

    return targetGroup;
  };

  const handleSearchSelect = async (option: PoiBrandSearch | null) => {
    if (!option) return;

    const targetGroup = getTargetGroup(option);
    updateActiveIndustryAndGroup(option.industry_code, option.group_code);
    const level = option.type ?? PoiLevel.BRAND;

    switch (level) {
      case PoiLevel.BRAND: {
        changeExpandCategory(option.category_code, true);
        handleSelectBrand(
          option.category_code,
          option.category_name,
          option,
          "add"
        );
        await attachBrandsToCategory({
          countryId: (selectedCountry ?? countryOptions?.[0])?.value,
          categoryCode: option.category_code,
          group: targetGroup
        });
        break;
      }
      case PoiLevel.CATEGORY: {
        await handleSelectCategory(
          option.category_code,
          option.category_name,
          true,
          targetGroup
        );
        break;
      }
      case PoiLevel.GROUP: {
        targetGroup?.poi_categories?.forEach(async (category) => {
          await handleSelectCategory(
            category.code,
            category.name,
            true,
            targetGroup
          );
        });
        break;
      }
    }
  };

  const { data: adminAreaHierarchyData } = useGetAdminAreaHierarchy(
    (selectedCountry ?? countryOptions?.[0])?.value,
    {
      enabled:
        isAdminTreeEnabled &&
        !!(selectedCountry ?? countryOptions?.[0])?.value &&
        selectedAdminAreaIds.length > 0
    }
  );

  const [poiPayload, setPoiPayload] = useState<PostPoiParametersPayload | null>(
    null
  );

  const { data: datasetsByPoiParameters, isLoading: isLoadingDatasets } =
    useGetDatasetsByPoiParameters(poiPayload!, {
      enabled: !!poiPayload
    });

  const handleContinue = async () => {
    // Extract unique codes from selectedBrands
    const industrySet = new Set<string>();
    const groupSet = new Set<string>();
    const categorySet = new Set<string>();
    const brandSet = new Set<string>();

    Object.values(selectedBrands).forEach((categoryGroup) => {
      categoryGroup.brands.forEach((brand) => {
        industrySet.add(brand.industry_code);
        groupSet.add(brand.group_code);
        categorySet.add(brand.category_code);
        brandSet.add(brand.brand_code);
      });
    });

    const adminAreaSingle = getDeepestAdminAreaCode(selectedAdminArea);

    // Get admin area codes
    const admAreaCodes = getSelectedAdminAreaCodesFromIds(
      adminAreaHierarchyData?.data || [],
      selectedAdminAreaIds
    );

    const payload: PostPoiParametersPayload = {
      country_id: (selectedCountry ?? countryOptions?.[0])?.value,
      industry_codes: Array.from(industrySet),
      group_codes: Array.from(groupSet),
      category_codes: Array.from(categorySet),
      brand_codes: Array.from(brandSet),
      adm_area_codes: isAdminTreeEnabled
        ? admAreaCodes.map((n) => n.full_code)
        : adminAreaSingle
    };

    setPoiPayload(payload);
  };

  useEffect(() => {
    if (poiPayload && !isLoadingDatasets && datasetsByPoiParameters) {
      setIsConfirmationOpen(true);
    }
  }, [poiPayload, isLoadingDatasets, datasetsByPoiParameters]);

  const poiParameterState: PoiParameterState = {
    selectedCountry,
    selectedAdminArea,
    selectedAdminAreaIds,
    activeIndustry,
    activeGroup,
    selectedBrands,
    selectedCategories,
    expandedCategories,
    sidebarExpandedCategories,
    poiPayload,
    selectedDatasetId,
    selectedDatasetIds
  };

  useEffect(() => {
    if (!historyPoiParameterState) return;

    const history = historyPoiParameterState;

    setSelectedCountry(history.selectedCountry ?? COUNTRY_DATA[1]);
    setSelectedAdminArea(history.selectedAdminArea ?? null);
    setSelectedAdminAreaIds(history.selectedAdminAreaIds ?? []);
    setActiveIndustry(history.activeIndustry ?? "11");
    setActiveGroup(history.activeGroup ?? "1101");
    setSelectedBrands(history.selectedBrands ?? {});
    setSelectedCategories(history.selectedCategories ?? new Set([]));
    setExpandedCategories(history.expandedCategories ?? new Set([]));
    setSidebarExpandedCategories(
      history.sidebarExpandedCategories ?? new Set([])
    );
    setPoiPayload(history.poiPayload ?? null);
    setSelectedDatasetId(history.selectedDatasetId ?? null);
    setSelectedDatasetIds(history.selectedDatasetIds ?? []);
    setIsConfirmationOpen(true);
    setShowPreview(true);
  }, [historyPoiParameterState]);

  const lockedDatasets =
    datasetsByPoiParameters?.datasets.filter((d) => !d.is_subscribed) || [];

  const noDataMessage = isBrandSearchQueryValid
    ? "Sorry, we couldn't find any results for that search. Try a different keyword or check your spelling."
    : undefined;

  // Check Global Admin Area Filter is Selected in POI Admin Area Filter
  const isGlobalFilterAreaSelected = isGlobalFilterAdminAreaSelected({
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled,
    globalAdminArea: globalAdminAreaSettings,
    globalSelectedAreaIds
  });

  const handleSwitchOverrideGlobalFilter = (e: boolean) => {
    setIsOverrideGlobalFilter(e);
    if (!e) {
      handleSyncSelectionGlobalAdminArea();
    }
  };

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden gap-4">
      {/* 1. Header: Country & Search */}
      <div className="flex gap-4 shrink-0">
        <div className="w-[200px] flex items-end">
          <Dropdown
            placeholder="Country"
            name="Country"
            data={countryOptions}
            value={selectedCountry ?? countryOptions?.[0]}
            isLoading={isLoadingCountryData}
            onSelect={(data) => {
              setSelectedCountry(data);
              setSelectedAdminArea(null);
              setSelectedAdminAreaIds([]);
              setExpandedCategories(new Set([])); // reset expanded categories
              setIsOverrideGlobalFilter(false); // reset global filter override when country changes
            }}
            className="w-full"
          />
        </div>
        <div className="flex-1 mt-4">
          <div className="h-full flex items-center">
            <SearchAutocomplete
              testId={`${TEST_ID_PREFIX}-brand-search-autocomplete`}
              groupedOptions={organizedSearchResults}
              getOptionLabel={(option) => option.brand_code}
              getKey={(option) =>
                `${option.brand_code}-${option.category_code}-${option.group_code}`
              }
              renderOption={renderSearchOptions}
              isSearchApi
              setSearchKeyword={setBrandSearchQuery}
              placeholder="Search by brand, category, or group"
              onSelect={handleSearchSelect}
              resetSearchOnSelect={false}
              suggestionItemClassName="hover:bg-white-smoke"
              showNoDataAsOption
              noDataTxt={noDataMessage}
              noDataTxtClassName="text-txtBody text-sm"
              onClear={() => setBrandSearchQuery("")}
              maxSuggestionHeight={420}
            />
          </div>
        </div>
      </div>

      {/* 2. Industry Ribbon */}
      <div className="shrink-0">
        <IndustryRibbon
          isLoading={isLoadingPoiParameters}
          industries={poiParameters?.data || []}
          activeIndustry={activeIndustry}
          onSelect={(industryCode) => {
            setActiveIndustry(industryCode);
            // Reset active group to first group of new category
            const firstGroup = getFirstGroupCode(industryCode);
            if (firstGroup) setActiveGroup(firstGroup);
          }}
        />
      </div>

      {/* 3. Main Content Area */}
      <div className="flex flex-1 gap-2.5 min-h-0">
        <PoiBrandTreeSelector
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          expandedCategories={expandedCategories}
          toggleCategoryExpand={toggleCategoryExpand}
          selectedBrands={selectedBrands}
          handleSelectCategory={handleSelectCategory}
          handleSelectBrand={(categoryCode, categoryName, brand, isChecked) =>
            handleSelectBrand(
              categoryCode,
              categoryName,
              brand,
              isChecked ? "add" : "remove"
            )
          }
          currentIndustry={currentIndustry}
          currentGroup={currentGroup}
          isLoading={isLoadingBrands}
        />

        <PoiSidebar
          selectedCountry={
            selectedCountry ?? {
              label: countryOptions?.[0]?.label || "",
              value: countryOptions?.[0]?.value
            }
          }
          selectedAdminArea={selectedAdminArea}
          selectedAdminAreaIds={selectedAdminAreaIds}
          selectedBrands={selectedBrands}
          sidebarExpandedCategories={sidebarExpandedCategories}
          toggleSidebarExpand={toggleSidebarExpand}
          handleDeleteCategory={handleDeleteCategory}
          handleDeleteBrand={handleDeleteBrand}
          onContinue={handleContinue}
          onSaveAdminArea={({ settings, selectedIds }) => {
            setSelectedAdminArea(settings);
            setSelectedAdminAreaIds(selectedIds);
          }}
          onDeleteAdminArea={() => {
            setSelectedAdminArea(null);
            setSelectedAdminAreaIds([]);
          }}
          isContinueDisabled={isContinueDisabled}
          selectedCategories={selectedCategories}
          isLoading={isLoadingDatasets}
          isOverrideGlobalFilter={isOverrideGlobalFilter}
          isGlobalFilterActive={isGlobalFilterAreaSelected}
          onSwitchOverrideGlobalFilter={handleSwitchOverrideGlobalFilter}
        />
      </div>

      <PoiConfirmationView
        data={datasetsByPoiParameters}
        open={isConfirmationOpen}
        onOpenChange={setIsConfirmationOpen}
        showPreview={showPreview}
        selectedAdminArea={selectedAdminArea}
        selectedAdminAreaIds={selectedAdminAreaIds}
        selectedCountry={selectedCountry ?? countryOptions?.[0]}
        selectedDatasetIds={selectedDatasetIds}
        setSelectedDatasetId={setSelectedDatasetId}
        setShowPreview={setShowPreview}
        setShowUnlockModal={setShowUnlockModal}
        setSelectedDatasetIds={setSelectedDatasetIds}
        isGlobalFilterAreaSelected={isGlobalFilterAreaSelected}
        isOverrideGlobalFilter={isOverrideGlobalFilter}
      />

      {showUnlockModal && (
        <UnlockPremiumModal
          title="Unlock Premium Dataset"
          description="To unlock this premium dataset, please contact your admin or reach out to our sales team to proceed with the purchase."
          contactUsDesc="If you have any questions about purchasing the dataset, feel free to get in touch:"
          emailSubject={`Request to Purchase Dataset - ${lockedDatasets.map((d) => d.dataset_name).join(", ")}`}
          emailBody={`Hi Bvarta team, I'm interested in accessing the dataset titled ${lockedDatasets.map((d) => d.dataset_name).join(", ")} from the source BVT. Please share details about licensing and pricing. Thanks!`}
          onClose={() => {
            setShowUnlockModal(false);
            setSelectedDatasetIds([]);
          }}
          onBack={() => {
            setShowUnlockModal(false);
            setIsConfirmationOpen(true);
          }}
          datasetSection={
            <div className="pt-2 space-y-2">
              {lockedDatasets.map((item) => (
                <DatasetItem
                  key={item.dataset_id}
                  name={item.dataset_name}
                  isLocked={true}
                />
              ))}
            </div>
          }
        />
      )}

      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onMouseUp={(e) => e.stopPropagation()}
        containerClassname="w-[90vw] h-[90vh] flex flex-col !shadow-none"
        className="!bg-transparent"
      >
        <Preview
          onBack={() => {
            setShowPreview(false);
          }}
          selectedDatasetId={selectedDatasetId}
          dataSource={"bvt"}
          withSearch={false}
          poiParameterState={poiParameterState}
        />
      </Modal>
    </div>
  );
};
